// Client-side custom (computed) columns for ExplorerDataGrid.
//
// Why this lives here, not in the grid component:
//   - The AST and evaluator are independent of MUI rendering and reused by
//     the columns-panel UI (preview), the grid materialization step, and the
//     URL serialization round-trip in tableState.ts.
//   - Keeping parsing/eval in a single module keeps the format/grammar
//     contract auditable in one place.
//
// Conventions matching the broader pipeline:
//   - Row values are strings. Numeric ops parse them via parseNumericValue,
//     which mirrors the grid's existing isNumericValue heuristic (handles a
//     trailing "%").
//   - Missing / non-finite results are represented by the literal string
//     "nan" so downstream code (renderer, type inference, percentile filter,
//     sort comparator) treats them identically to backend-returned NaNs.

// ---------------------------------------------------------------------------
// AST
// ---------------------------------------------------------------------------

export type BinaryOp = "+" | "-" | "*" | "/";

export type ExprNode =
  | { t: "col"; f: string }
  | { t: "num"; v: number }
  | { t: "neg"; e: ExprNode }
  | { t: "op"; o: BinaryOp; l: ExprNode; r: ExprNode }
  // Reserved for future expansion (min/max/abs/if). Validator rejects in MVP.
  | { t: "fn"; n: string; a: ExprNode[] };

export type CustomColumnFormat = {
  decimals?: 0 | 1 | 2;
  percent?: boolean;
  signed?: boolean; // prepend "+" to positive values (useful for diff/variance columns)
};

export type CustomColumn = {
  field: string;
  headerName?: string;
  expr: ExprNode;
  format?: CustomColumnFormat;
};

// ---------------------------------------------------------------------------
// Numeric parsing — shared with grid's existing pipeline
// ---------------------------------------------------------------------------

/**
 * Parse a row-cell value to a finite number, mirroring the conventions of
 * the existing isNumericValue heuristic in ExplorerDataGrid:
 *   - null / undefined / "" → NaN
 *   - "NaN" / "nan" / "inf" / "--" → NaN
 *   - "45.2%" → 45.2 (treats trailing % as decoration)
 *   - boolean → NaN
 *   - "abc" → NaN
 */
export function parseNumericValue(value: unknown): number {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "boolean") return NaN;
  if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
  if (typeof value !== "string") return NaN;
  if (value === "NaN" || value === "nan" || value === "inf" || value === "--") {
    return NaN;
  }
  const trimmed = value.endsWith("%") ? value.slice(0, -1) : value;
  const n = parseFloat(trimmed);
  if (!Number.isFinite(n)) return NaN;
  // Reject "100abc" cases — parseFloat accepts trailing junk.
  // Allow scientific notation, leading sign.
  if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed.trim())) {
    return NaN;
  }
  return n;
}

// ---------------------------------------------------------------------------
// Tokenizer
// ---------------------------------------------------------------------------

type Token =
  | { type: "num"; value: number }
  | { type: "col"; name: string }
  | { type: "op"; value: BinaryOp }
  | { type: "lparen" }
  | { type: "rparen" };

function tokenize(input: string): { tokens: Token[] } | { error: string } {
  const tokens: Token[] = [];
  let i = 0;
  while (i < input.length) {
    const ch = input[i];

    if (ch === " " || ch === "\t" || ch === "\n" || ch === "\r") {
      i++;
      continue;
    }

    // Column reference: ${ColumnName}
    if (ch === "$" && input[i + 1] === "{") {
      const end = input.indexOf("}", i + 2);
      if (end === -1) {
        return { error: "Unclosed column reference (missing '}')" };
      }
      const name = input.slice(i + 2, end);
      if (name.length === 0) {
        return { error: "Empty column reference '${}'" };
      }
      tokens.push({ type: "col", name });
      i = end + 1;
      continue;
    }

    // Numeric literal
    if ((ch >= "0" && ch <= "9") || ch === ".") {
      let j = i;
      while (
        j < input.length &&
        ((input[j] >= "0" && input[j] <= "9") ||
          input[j] === "." ||
          input[j] === "e" ||
          input[j] === "E" ||
          ((input[j] === "+" || input[j] === "-") &&
            (input[j - 1] === "e" || input[j - 1] === "E")))
      ) {
        j++;
      }
      const raw = input.slice(i, j);
      const n = parseFloat(raw);
      if (!Number.isFinite(n)) {
        return { error: `Invalid number "${raw}"` };
      }
      tokens.push({ type: "num", value: n });
      i = j;
      continue;
    }

    if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
      tokens.push({ type: "op", value: ch });
      i++;
      continue;
    }
    if (ch === "(") {
      tokens.push({ type: "lparen" });
      i++;
      continue;
    }
    if (ch === ")") {
      tokens.push({ type: "rparen" });
      i++;
      continue;
    }

    return {
      error: `Unexpected character "${ch}" at position ${i}. Reference columns as \${ColumnName}.`,
    };
  }
  return { tokens };
}

// ---------------------------------------------------------------------------
// Parser (Shunting-Yard → AST)
// ---------------------------------------------------------------------------

const PRECEDENCE: Record<BinaryOp, number> = {
  "+": 1,
  "-": 1,
  "*": 2,
  "/": 2,
};

export function parseFormula(
  input: string,
): { ast: ExprNode } | { error: string } {
  const tokRes = tokenize(input);
  if ("error" in tokRes) return tokRes;
  const { tokens } = tokRes;
  if (tokens.length === 0) return { error: "Formula is empty" };

  // Shunting-Yard with unary minus support.
  // We track whether the previous token allows a value (false = previous was
  // value/closing-paren, so a "-" must be subtraction; true = previous was
  // op/lparen/start, so "-" is unary).
  const output: ExprNode[] = [];
  const opStack: Array<
    { type: "op"; value: BinaryOp } | { type: "lparen" } | { type: "neg" }
  > = [];

  const applyOp = (
    op: { type: "op"; value: BinaryOp } | { type: "neg" },
  ): string | null => {
    if (op.type === "neg") {
      const e = output.pop();
      if (!e) return "Missing operand for unary minus";
      output.push({ t: "neg", e });
      return null;
    }
    const r = output.pop();
    const l = output.pop();
    if (!l || !r) return `Missing operand for "${op.value}"`;
    output.push({ t: "op", o: op.value, l, r });
    return null;
  };

  let expectValue = true;
  for (const tok of tokens) {
    if (tok.type === "num") {
      if (!expectValue) return { error: "Unexpected number" };
      output.push({ t: "num", v: tok.value });
      expectValue = false;
    } else if (tok.type === "col") {
      if (!expectValue) return { error: `Unexpected column "${tok.name}"` };
      output.push({ t: "col", f: tok.name });
      expectValue = false;
    } else if (tok.type === "op") {
      if (expectValue) {
        // Unary plus is a no-op; unary minus pushes a neg marker
        if (tok.value === "-") {
          opStack.push({ type: "neg" });
        } else if (tok.value !== "+") {
          return { error: `Unexpected operator "${tok.value}"` };
        }
        // expectValue stays true
      } else {
        // Binary operator — pop operators of >= precedence
        while (opStack.length > 0) {
          const top = opStack[opStack.length - 1];
          if (top.type === "lparen") break;
          if (
            top.type === "neg" ||
            (top.type === "op" &&
              PRECEDENCE[top.value] >= PRECEDENCE[tok.value])
          ) {
            opStack.pop();
            const err = applyOp(top as any);
            if (err) return { error: err };
          } else {
            break;
          }
        }
        opStack.push({ type: "op", value: tok.value });
        expectValue = true;
      }
    } else if (tok.type === "lparen") {
      if (!expectValue) return { error: "Unexpected '('" };
      opStack.push({ type: "lparen" });
      // expectValue stays true
    } else if (tok.type === "rparen") {
      if (expectValue) return { error: "Unexpected ')'" };
      let foundLparen = false;
      while (opStack.length > 0) {
        const top = opStack.pop()!;
        if (top.type === "lparen") {
          foundLparen = true;
          break;
        }
        const err = applyOp(top as any);
        if (err) return { error: err };
      }
      if (!foundLparen) return { error: "Unmatched ')'" };
      expectValue = false;
    }
  }

  while (opStack.length > 0) {
    const top = opStack.pop()!;
    if (top.type === "lparen") return { error: "Unmatched '('" };
    const err = applyOp(top as any);
    if (err) return { error: err };
  }

  if (output.length !== 1) return { error: "Malformed expression" };
  return { ast: output[0] };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export const RESERVED_FIELDS = new Set(["Rank", "actions"]);
export const FIELD_NAME_RE = /^[A-Za-z][A-Za-z0-9 _\-/%]*$/;

export type ValidationContext = {
  baseFields: Set<string>; // current columns from the data + Rank/actions
  customFields: Set<string>; // currently-defined custom column field names (excluding the one being edited)
};

export function collectColumnRefs(node: ExprNode, out: Set<string>): void {
  switch (node.t) {
    case "col":
      out.add(node.f);
      return;
    case "num":
      return;
    case "neg":
      collectColumnRefs(node.e, out);
      return;
    case "op":
      collectColumnRefs(node.l, out);
      collectColumnRefs(node.r, out);
      return;
    case "fn":
      node.a.forEach((a) => collectColumnRefs(a, out));
      return;
  }
}

export function validateAst(
  ast: ExprNode,
  ctx: ValidationContext,
): string | null {
  // Reject reserved nodes
  let err: string | null = null;
  const walk = (node: ExprNode): void => {
    if (err) return;
    if (node.t === "fn") {
      err = `Functions are not yet supported (got "${node.n}")`;
      return;
    }
    if (node.t === "neg") {
      walk(node.e);
      return;
    }
    if (node.t === "op") {
      walk(node.l);
      if (!err) walk(node.r);
      return;
    }
    if (node.t === "col") {
      // Refs to custom columns are now allowed; the cycle check
      // (detectCycle) runs separately at save time.
      if (ctx.customFields.has(node.f)) return;
      if (!ctx.baseFields.has(node.f)) {
        err = `Unknown column "${node.f}"`;
        return;
      }
    }
  };
  walk(ast);
  return err;
}

export function validateFieldName(
  field: string,
  baseFields: Set<string>,
  customFields: Set<string>,
): string | null {
  if (!field) return "Name is required";
  if (!FIELD_NAME_RE.test(field)) {
    return "Name must start with a letter and contain only letters, digits, spaces, _, -, /, %";
  }
  if (RESERVED_FIELDS.has(field)) {
    return `"${field}" is a reserved name`;
  }
  if (baseFields.has(field)) {
    return `"${field}" is already an existing column`;
  }
  if (customFields.has(field)) {
    return `"${field}" is already a custom column`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Evaluation
// ---------------------------------------------------------------------------

const NAN_STR = "nan";

function evalNumeric(
  node: ExprNode,
  row: Record<string, any>,
): number | typeof NaN {
  switch (node.t) {
    case "num":
      return node.v;
    case "col":
      return parseNumericValue(row[node.f]);
    case "neg": {
      const v = evalNumeric(node.e, row);
      return Number.isFinite(v) ? -v : NaN;
    }
    case "op": {
      const l = evalNumeric(node.l, row);
      const r = evalNumeric(node.r, row);
      if (!Number.isFinite(l) || !Number.isFinite(r)) return NaN;
      let out: number;
      switch (node.o) {
        case "+":
          out = l + r;
          break;
        case "-":
          out = l - r;
          break;
        case "*":
          out = l * r;
          break;
        case "/":
          if (r === 0) return NaN;
          out = l / r;
          break;
      }
      return Number.isFinite(out) ? out : NaN;
    }
    case "fn":
      return NaN;
  }
}

/**
 * Evaluate a custom column's expression against a single row.
 * Returns either a stringified finite number or the literal "nan".
 */
export function evaluateColumn(
  cc: CustomColumn,
  row: Record<string, any>,
): string {
  const v = evalNumeric(cc.expr, row);
  if (!Number.isFinite(v)) return NAN_STR;
  return String(v);
}

/**
 * Topologically sort custom columns by dependency.
 * Returns ordered columns (dependencies first), or null on cycle.
 */
export function topoSortColumns(
  customColumns: CustomColumn[],
): CustomColumn[] | null {
  const byField = new Map<string, CustomColumn>();
  for (const c of customColumns) byField.set(c.field, c);

  const visited = new Set<string>();
  const visiting = new Set<string>();
  const order: CustomColumn[] = [];
  let cycle = false;

  const visit = (field: string): void => {
    if (cycle) return;
    if (visited.has(field)) return;
    if (visiting.has(field)) {
      cycle = true;
      return;
    }
    const cc = byField.get(field);
    if (!cc) return; // base column ref; not part of the DAG
    visiting.add(field);
    const refs = new Set<string>();
    collectColumnRefs(cc.expr, refs);
    refs.forEach((r) => visit(r));
    visiting.delete(field);
    visited.add(field);
    order.push(cc);
  };

  for (const c of customColumns) {
    visit(c.field);
    if (cycle) return null;
  }
  return order;
}

/**
 * Materialize all custom columns into a copy of the row data.
 * Custom columns are evaluated in dependency order, so a column referencing
 * another custom column reads that value from the partially-built row.
 */
export function materializeCustomColumns<T extends Record<string, any>>(
  rows: T[],
  customColumns: CustomColumn[],
): T[] {
  if (customColumns.length === 0) return rows;
  // Cycle should have been caught at edit time; fall back to declared order
  // if it slips through (downstream cells will read undefined → "nan").
  const ordered = topoSortColumns(customColumns) ?? customColumns;
  return rows.map((row) => {
    const out: Record<string, any> = { ...row };
    for (const cc of ordered) {
      out[cc.field] = evaluateColumn(cc, out);
    }
    return out as T;
  });
}

/**
 * Detect whether adding/replacing a column would introduce a cycle.
 * Pass `excludeOriginal` when editing an existing column so its OLD expression
 * doesn't shadow the proposed new one.
 */
export function detectCycle(args: {
  proposedField: string;
  proposedExpr: ExprNode;
  existing: CustomColumn[];
  excludeOriginal?: string;
}): string | null {
  const deps = new Map<string, Set<string>>();
  for (const c of args.existing) {
    if (c.field === args.excludeOriginal) continue;
    const refs = new Set<string>();
    collectColumnRefs(c.expr, refs);
    deps.set(c.field, refs);
  }
  const newRefs = new Set<string>();
  collectColumnRefs(args.proposedExpr, newRefs);
  deps.set(args.proposedField, newRefs);

  const visited = new Set<string>();
  const stack = new Set<string>();
  let cycleVia: string | null = null;
  const visit = (field: string): boolean => {
    if (cycleVia) return false;
    if (visited.has(field)) return true;
    if (stack.has(field)) {
      cycleVia = field;
      return false;
    }
    stack.add(field);
    const refs = deps.get(field);
    if (refs) {
      const refList: string[] = [];
      refs.forEach((r) => refList.push(r));
      for (const r of refList) {
        if (deps.has(r) && !visit(r)) return false;
      }
    }
    stack.delete(field);
    visited.add(field);
    return true;
  };
  visit(args.proposedField);
  if (cycleVia) {
    return cycleVia === args.proposedField
      ? `"${args.proposedField}" cannot reference itself`
      : `Circular reference: "${args.proposedField}" eventually depends on "${cycleVia}"`;
  }
  return null;
}

/**
 * Compute the set of custom-column fields that transitively reference
 * `targetField`. Used by the formula picker to suppress columns that would
 * create a cycle if referenced.
 */
export function dependentsOf(
  targetField: string,
  customColumns: CustomColumn[],
): Set<string> {
  const deps = new Map<string, Set<string>>();
  for (const c of customColumns) {
    if (c.field === targetField) continue;
    const refs = new Set<string>();
    collectColumnRefs(c.expr, refs);
    deps.set(c.field, refs);
  }
  const dependents = new Set<string>();
  // Iterate to fixed point; use Set.size as the change signal so the inner
  // forEach closure mutates only via Set methods (no outer-var reassignment,
  // which `no-loop-func` flags as unsafe).
  while (true) {
    const before = dependents.size;
    deps.forEach((refs, field) => {
      if (dependents.has(field)) return;
      let hits = false;
      refs.forEach((r) => {
        if (r === targetField || dependents.has(r)) hits = true;
      });
      if (hits) dependents.add(field);
    });
    if (dependents.size === before) break;
  }
  return dependents;
}

// ---------------------------------------------------------------------------
// Display / format
// ---------------------------------------------------------------------------

/**
 * Mirror of TeamReportDataTable/utils.ts:commonRenderValue heuristic — used
 * when no explicit decimals are configured.
 */
function autoDecimals(absVal: number): number {
  return absVal < 5 ? 2 : 1;
}

/**
 * Format a custom column's evaluated string value for display, applying
 * decimals and percent-display per the format spec. Returns "––" for "nan".
 */
export function formatCustomValue(
  raw: string,
  format: CustomColumnFormat | undefined,
): string {
  if (raw === NAN_STR || raw === "" || raw == null) return "––";
  const n = parseFloat(raw);
  if (!Number.isFinite(n)) return "––";
  const percent = !!format?.percent;
  const value = percent ? n * 100 : n;
  const decimals =
    format?.decimals !== undefined
      ? format.decimals
      : autoDecimals(Math.abs(value));
  const body = value.toFixed(decimals);
  // toFixed already prepends "-" for negatives; "+" only applies to strictly
  // positive values (zero stays unsigned).
  const sign = format?.signed && value > 0 ? "+" : "";
  return percent ? `${sign}${body}%` : `${sign}${body}`;
}

// ---------------------------------------------------------------------------
// Slug helper — derive a stable field ID from a user-entered display name
// ---------------------------------------------------------------------------

/**
 * Convert a free-form display name into a column field ID:
 *   - Trim
 *   - Replace whitespace runs with "_"
 *   - Strip characters outside [A-Za-z0-9_-/%]
 *   - Ensure leading character is a letter (prefix "c_" if not)
 *   - Append numeric suffix (_2, _3, ...) until unique against `taken`
 */
export function deriveFieldId(displayName: string, taken: Set<string>): string {
  const cleanedRaw = displayName
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^A-Za-z0-9_\-/%]/g, "");
  let cleaned = cleanedRaw || "column";
  if (!/^[A-Za-z]/.test(cleaned)) cleaned = `c_${cleaned}`;
  if (!taken.has(cleaned)) return cleaned;
  let i = 2;
  while (taken.has(`${cleaned}_${i}`)) i++;
  return `${cleaned}_${i}`;
}

// ---------------------------------------------------------------------------
// Pretty-print (for editor preview / panel listing)
// ---------------------------------------------------------------------------

export function formulaToString(node: ExprNode): string {
  return printNode(node, 0);
}

function printNode(node: ExprNode, parentPrec: number): string {
  switch (node.t) {
    case "num":
      return String(node.v);
    case "col":
      return `\${${node.f}}`;
    case "neg":
      return `-${printNode(node.e, 3)}`;
    case "op": {
      const prec = PRECEDENCE[node.o];
      const s = `${printNode(node.l, prec)} ${node.o} ${printNode(node.r, prec + 1)}`;
      return prec < parentPrec ? `(${s})` : s;
    }
    case "fn":
      return `${node.n}(${node.a.map((a) => printNode(a, 0)).join(", ")})`;
  }
}
